// Training Record Form Template
// Based on SOP sections 7.1 and 7.2 Training & Continuous Improvement

const getTrainingRecordFormTemplate = (t) => ({
  formType: 'training-record',
  title: {
    en: 'Training Record Form',
    th: 'แบบบันทึกการฝึกอบรม'
  },
  description: {
    en: 'Standardized form for documenting training sessions, simulation exercises, and continuous improvement activities',
    th: 'แบบฟอร์มมาตรฐานสำหรับบันทึกการฝึกอบรม การจำลองสถานการณ์ และกิจกรรมการปรับปรุงอย่างต่อเนื่อง'
  },
  fields: [
    // Basic Training Information
    {
      name: 'trainingDate',
      label: {
        en: 'Training Date',
        th: 'วันที่ฝึกอบรม'
      },
      type: 'date',
      required: true,
      tooltip: {
        en: 'Date when the training session was conducted. Example: 2023-10-15',
        th: 'วันที่จัดการฝึกอบรม ตัวอย่าง: 2023-10-15'
      }
    },
    {
      name: 'trainingType',
      label: {
        en: 'Training Type',
        th: 'ประเภทการฝึกอบรม'
      },
      type: 'select',
      options: [
        { value: 'sop-refresher', label: { en: 'SOP Refresher', th: 'การทบทวนข้อกำหนด' } },
        { value: 'simulation', label: { en: 'Simulation Exercise', th: 'การจำลองสถานการณ์' } },
        { value: 'emergency-response', label: { en: 'Emergency Response', th: 'การตอบสนองฉุกเฉิน' } },
        { value: 'hazard-identification', label: { en: 'Hazard Identification', th: 'การระบุอันตราย' } },
        { value: 'compliance', label: { en: 'Compliance Training', th: 'การฝึกอบรมด้านการปฏิบัติตามข้อกำหนด' } },
        { value: 'quality-assurance', label: { en: 'Quality Assurance', th: 'การประกันคุณภาพ' } },
        { value: 'new-hire', label: { en: 'New Hire Orientation', th: 'การปฐมนิเทศพนักงานใหม่' } },
        { value: 'specialized', label: { en: 'Specialized Skills Training', th: 'การฝึกอบรมทักษะเฉพาะทาง' } },
        { value: 'refresher', label: { en: 'General Refresher', th: 'การทบทวนทั่วไป' } },
        { value: 'other', label: { en: 'Other', th: 'อื่น ๆ' } }
      ],
      required: true,
      tooltip: {
        en: 'Category that best describes the training session. SOP Refresher: Annual review of safety procedures. Simulation: Hands-on emergency drills. Emergency Response: Fire, chemical spill, or medical emergency protocols.',
        th: 'หมวดหมู่ที่อธิบายการฝึกอบรมได้ดีที่สุด การทบทวนข้อกำหนด: การทบทวนขั้นตอนความปลอดภัยเป็นประจำ การจำลองสถานการณ์: การฝึกฝนเหตุฉุกเฉินแบบลงมือทำ การตอบสนองฉุกเฉิน: โพรโทคอลไฟ สารเคมีหก หรือเหตุฉุกเฉินทางการแพทย์'
      }
    },
    {
      name: 'trainerName',
      label: {
        en: 'Trainer Name',
        th: 'ชื่อผู้ฝึก'
      },
      type: 'text',
      required: true,
      tooltip: {
        en: 'Full name of the person conducting the training. Example: Dr. Amanda Rodriguez',
        th: 'ชื่อ-นามสกุลของผู้จัดการฝึกอบรม ตัวอย่าง: ดร. อาแมนดา โรดริเกซ'
      }
    },
    {
      name: 'trainerPosition',
      label: {
        en: 'Trainer Position',
        th: 'ตำแหน่งของผู้ฝึก'
      },
      type: 'text',
      required: true,
      tooltip: {
        en: 'Job title or role of the trainer. Example: Senior Safety Officer',
        th: 'ตำแหน่งงานหรือบทบาทของผู้ฝึก ตัวอย่าง: เจ้าหน้าที่ความปลอดภัยอาวุโส'
      }
    },
    {
      name: 'trainingTopic',
      label: {
        en: 'Training Topic',
        th: 'หัวข้อการฝึกอบรม'
      },
      type: 'text',
      required: true,
      placeholder: {
        en: 'e.g., PPE Usage, Chemical Handling, Emergency Evacuation...',
        th: 'เช่น การใช้ PPE การจัดการสารเคมี การอพยพฉุกเฉิน...'
      },
      tooltip: {
        en: 'Specific subject matter covered in the training. Be as detailed as possible. Example: PPE Usage for Pesticide Application - Proper Gloving, Respirator Fitting, and Contamination Prevention',
        th: 'เนื้อหาเฉพาะที่ครอบคลุมในการฝึกอบรม ระบุรายละเอียดให้มากที่สุด ตัวอย่าง: การใช้ PPE สำหรับการใช้สารกำจัดศัตรูพืช - การสวมถุงมืออย่างเหมาะสม การพอดีของเครื่องกรองอากาศ และการป้องกันการปนเปื้อน'
      }
    },
    {
      name: 'trainingDuration',
      label: {
        en: 'Training Duration (hours)',
        th: 'ระยะเวลาการฝึกอบรม (ชั่วโมง)'
      },
      type: 'number',
      min: 0.5,
      step: 0.5,
      required: true,
      tooltip: {
        en: 'Total length of the training session in hours. Use 0.5 increments. Example: 2.5 hours',
        th: 'ระยะเวลาการฝึกอบรมทั้งหมดเป็นชั่วโมง ใช้เพิ่มทีละ 0.5 ชั่วโมง ตัวอย่าง: 2.5 ชั่วโมง'
      }
    },

    // Participants Section
    {
      name: 'sectionHeader_participants',
      label: {
        en: '=== Training Participants ===',
        th: '=== ผู้เข้าร่วมการฝึกอบรม ==='
      },
      type: 'header'
    },
    {
      name: 'participants',
      label: {
        en: 'Participants',
        th: 'ผู้เข้าร่วม'
      },
      type: 'textarea',
      rows: 6,
      placeholder: {
        en: 'List all participants (Name, Position, Department)...',
        th: 'ระบุผู้เข้าร่วมทั้งหมด (ชื่อ ตำแหน่ง แผนก)...'
      },
      required: true,
      tooltip: {
        en: 'Complete list of all training participants with full names, positions, and departments. Example: 1. John Smith, Field Technician, Operations Department. 2. Maria Garcia, Safety Coordinator, HSE Division.',
        th: 'รายชื่อผู้เข้าร่วมการฝึกอบรมทั้งหมดพร้อมชื่อ-นามสกุล ตำแหน่ง และแผนก ตัวอย่าง: 1. จอห์น สมิธ ช่างเทคนิคในสนาม แผนกปฏิบัติการ 2. มารีอา การ์เซีย ผู้ประสานงานด้านความปลอดภัย แผนก HSE'
      }
    },
    {
      name: 'participantCount',
      label: {
        en: 'Total Number of Participants',
        th: 'จำนวนผู้เข้าร่วมทั้งหมด'
      },
      type: 'number',
      min: 1,
      required: true,
      tooltip: {
        en: 'Total count of training participants. Must match the list above. Example: 15',
        th: 'จำนวนผู้เข้าร่วมการฝึกอบรมทั้งหมด ต้องตรงกับรายชื่อด้านบน ตัวอย่าง: 15'
      }
    },

    // Training Content Section
    {
      name: 'sectionHeader_content',
      label: {
        en: '=== Training Content ===',
        th: '=== เนื้อหาการฝึกอบรม ==='
      },
      type: 'header'
    },
    {
      name: 'trainingObjectives',
      label: {
        en: 'Training Objectives',
        th: 'วัตถุประสงค์การฝึกอบรม'
      },
      type: 'textarea',
      rows: 4,
      placeholder: {
        en: 'List specific learning objectives for this training session...',
        th: 'ระบุวัตถุประสงค์การเรียนรู้เฉพาะสำหรับการฝึกอบรมครั้งนี้...'
      },
      required: true,
      tooltip: {
        en: 'Clear, measurable goals that participants should achieve by the end of training. Use action verbs like identify, demonstrate, explain, or perform. Example: By the end of this session, participants will be able to identify 5 common field hazards and explain proper PPE selection for each.',
        th: 'เป้าหมายที่ชัดเจนและสามารถวัดได้ที่ผู้เข้าร่วมควรบรรลุเมื่อสิ้นสุดการฝึกอบรม ใช้กริยามาตรฐานเช่น ระบุ แสดงให้เห็น อธิบาย หรือดำเนินการ ตัวอย่าง: เมื่อสิ้นสุดเซสชันนี้ ผู้เข้าร่วมจะสามารถระบุอันตรายในสนามทั่วไป 5 ประการและอธิบายการเลือก PPE ที่เหมาะสมสำหรับแต่ละข้อ'
      }
    },
    {
      name: 'trainingMaterials',
      label: {
        en: 'Training Materials Used',
        th: 'วัสดุการฝึกอบรมที่ใช้'
      },
      type: 'textarea',
      rows: 4,
      placeholder: {
        en: 'List materials, presentations, videos, handouts, etc...',
        th: 'ระบุวัสดุ สื่อการนำเสนอ วิดีโอ ใบพิมพ์ เป็นต้น...'
      },
      tooltip: {
        en: 'Complete inventory of all materials used during the training. Include presentation slides, handouts, videos, props, and reference documents. Example: PPE Training PowerPoint (v2.1), Hazard Identification Checklist, Sample Respirator Fittings',
        th: 'รายการสิ่งของทั้งหมดที่ใช้ในการฝึกอบรม รวมถึงสไลด์นำเสนอ ใบพิมพ์ วิดีโอ อุปกรณ์ประกอบ และเอกสารอ้างอิง ตัวอย่าง: สไลด์ฝึกอบรม PPE (v2.1) แบบฟอร์มตรวจสอบการระบุอันตราย ตัวอย่างการพอดีของเครื่องกรองอากาศ'
      }
    },
    {
      name: 'trainingMethods',
      label: {
        en: 'Training Methods',
        th: 'วิธีการฝึกอบรม'
      },
      type: 'select',
      options: [
        { value: 'lecture', label: { en: 'Lecture', th: 'บรรยาย' } },
        { value: 'hands-on', label: { en: 'Hands-on Practice', th: 'ปฏิบัติจริง' } },
        { value: 'simulation', label: { en: 'Simulation/Role Play', th: 'การจำลอง/แสดงบทบาท' } },
        { value: 'discussion', label: { en: 'Group Discussion', th: 'อภิปรายกลุ่ม' } },
        { value: 'video', label: { en: 'Video Presentation', th: 'การนำเสนอวิดีโอ' } },
        { value: 'case-study', label: { en: 'Case Study Analysis', th: 'การวิเคราะห์กรณีศึกษา' } },
        { value: 'demonstration', label: { en: 'Demonstration', th: 'การสาธิต' } },
        { value: 'workshop', label: { en: 'Workshop', th: 'เวิร์กช็อป' } },
        { value: 'mixed', label: { en: 'Mixed Methods', th: 'วิธีการผสมผสาน' } }
      ],
      required: true,
      tooltip: {
        en: 'Primary instructional approach used during the session. Lecture: Instructor-led presentation. Hands-on: Physical practice with equipment. Simulation: Realistic scenario enactment. Mixed Methods: Combination of approaches for maximum engagement.',
        th: 'แนวทางการสอนหลักที่ใช้ในระหว่างเซสชัน บรรยาย: การนำเสนอภายใต้การนำของผู้สอน ปฏิบัติจริง: การฝึกฝนด้วยอุปกรณ์ จำลองสถานการณ์: การ enact สถานการณ์ที่เหมือนจริง วิธีการผสมผสาน: การรวมกันของแนวทางเพื่อการมีส่วนร่วมสูงสุด'
      }
    },

    // Simulation/Assessment Section (if applicable)
    {
      name: 'sectionHeader_simulation',
      label: {
        en: '=== Simulation/Assessment (if applicable) ===',
        th: '=== การจำลองสถานการณ์/การประเมิน (หากระบุไว้) ==='
      },
      type: 'header'
    },
    {
      name: 'simulationScenario',
      label: {
        en: 'Simulation Scenario',
        th: 'สถานการณ์การจำลอง'
      },
      type: 'textarea',
      rows: 4,
      placeholder: {
        en: 'Describe the simulation scenario used in training...',
        th: 'อธิบายสถานการณ์การจำลองที่ใช้ในการฝึกอบรม...'
      },
      tooltip: {
        en: 'Detailed description of any simulation exercise conducted. Include scenario setup, roles assigned, safety measures, and learning objectives. Example: Chemical Spill Response - 5-gallon drum rupture in processing area, wind direction 180°, 3 trainees assigned spill response roles.',
        th: 'คำอธิบายโดยละเอียดของการจำลองสถานการณ์ใดๆ ที่ดำเนินการ รวมถึงการตั้งค่าสถานการณ์ บทบาทที่กำหนด มาตรการความปลอดภัย และวัตถุประสงค์การเรียนรู้ ตัวอย่าง: การตอบสนองต่อการหกของสารเคมี - การแตกของถังขนาด 5 แกลลอนในพื้นที่แปรรูป ทิศทางลม 180° ผู้เข้าร่วม 3 คนได้รับมอบหมายบทบาทการตอบสนองต่อการหก'
      }
    },
    {
      name: 'simulationPerformance',
      label: {
        en: 'Overall Simulation Performance',
        th: 'ผลการจำลองสถานการณ์โดยรวม'
      },
      type: 'select',
      options: [
        { value: 'excellent', label: { en: 'Excellent', th: 'ยอดเยี่ยม' } },
        { value: 'good', label: { en: 'Good', th: 'ดี' } },
        { value: 'fair', label: { en: 'Fair', th: 'พอใช้' } },
        { value: 'poor', label: { en: 'Poor', th: 'แย่' } }
      ],
      tooltip: {
        en: 'Overall rating of participant performance during simulation exercises. Consider response time, accuracy, teamwork, and adherence to procedures. Excellent: Prompt, accurate, coordinated responses with clear communication.',
        th: 'การให้คะแนนโดยรวมของผลการดำเนินการของผู้เข้าร่วมระหว่างการจำลองสถานการณ์ พิจารณาเวลาตอบสนอง ความแม่นยำ การทำงานเป็นทีม และการปฏิบัติตามขั้นตอน ยอดเยี่ยม: การตอบสนองที่รวดเร็ว แม่นยำ การประสานงานที่ดี การสื่อสารที่ชัดเจน'
      }
    },
    {
      name: 'performanceComments',
      label: {
        en: 'Performance Comments',
        th: 'ข้อคิดเห็นเกี่ยวกับผลการดำเนินงาน'
      },
      type: 'textarea',
      rows: 4,
      placeholder: {
        en: 'General comments on participant performance during simulation...',
        th: 'ข้อคิดเห็นทั่วไปเกี่ยวกับผลการดำเนินงานของผู้เข้าร่วมระหว่างการจำลองสถานการณ์...'
      },
      tooltip: {
        en: 'Specific observations about strengths demonstrated and areas needing improvement. Include examples of excellent performance and constructive feedback for development. Example: Trainee #3 showed exceptional leadership coordinating evacuation. Several participants need additional practice with respirator donning/doffing procedures.',
        th: 'ข้อสังเกตเฉพาะเกี่ยวกับจุดแข็งที่แสดงออกและพื้นที่ที่ต้องปรับปรุง รวมถึงตัวอย่างของผลการดำเนินการที่ยอดเยี่ยมและคำติชมเชิงสร้างสรรค์สำหรับการพัฒนา ตัวอย่าง: ผู้เข้าร่วม #3 แสดงความเป็นผู้นำที่โดดเด่นในการประสานงานการอพยพ ผู้เข้าร่วมหลายคนต้องการการฝึกฝนเพิ่มเติมเกี่ยวกับขั้นตอนการสวมใส่/ถอดเครื่องกรองอากาศ'
      }
    },

    // Assessment/Evaluation Section
    {
      name: 'sectionHeader_assessment',
      label: {
        en: '=== Assessment/Evaluation ===',
        th: '=== การประเมิน ==='
      },
      type: 'header'
    },
    {
      name: 'knowledgeAssessment',
      label: {
        en: 'Knowledge Assessment Method',
        th: 'วิธีการประเมินความรู้'
      },
      type: 'select',
      options: [
        { value: 'quiz', label: { en: 'Quiz/Test', th: 'แบบทดสอบ' } },
        { value: 'practical', label: { en: 'Practical Demonstration', th: 'การแสดงออกอย่างเป็นรูปธรรม' } },
        { value: 'observation', label: { en: 'Direct Observation', th: 'การสังเกตโดยตรง' } },
        { value: 'discussion', label: { en: 'Discussion/Q&A', th: 'การอภิปราย/ถาม-ตอบ' } },
        { value: 'portfolio', label: { en: 'Portfolio/Work Sample', th: 'ผลงาน/ตัวอย่างงาน' } },
        { value: 'peer', label: { en: 'Peer Evaluation', th: 'การประเมินโดยเพื่อน' } },
        { value: 'self', label: { en: 'Self-Assessment', th: 'การประเมินตนเอง' } },
        { value: 'none', label: { en: 'None', th: 'ไม่มี' } }
      ],
      tooltip: {
        en: 'Method used to evaluate participant comprehension and skill acquisition. Quiz/Test: Written examination. Practical: Hands-on demonstration of learned skills. Observation: Direct monitoring of performance. Q&A: Interactive questioning session.',
        th: 'วิธีการที่ใช้ประเมินความเข้าใจและทักษะที่ได้รับของผู้เข้าร่วม แบบทดสอบ: การสอบเขียน การแสดงออก: การสาธิตทักษะที่เรียนรู้ด้วยตนเอง การสังเกต: การตรวจสอบผลการดำเนินงานโดยตรง ถาม-ตอบ: เซสชันคำถามตอบโต้ตอบ'
      }
    },
    {
      name: 'passingScore',
      label: {
        en: 'Passing Score (%)',
        th: 'คะแนนผ่าน (%)'
      },
      type: 'number',
      min: 0,
      max: 100,
      tooltip: {
        en: 'Minimum score required to pass the assessment. Industry standard is typically 80%. Example: 80',
        th: 'คะแนนขั้นต่ำที่ต้องผ่านการประเมิน มาตรฐานอุตสาหกรรมโดยทั่วไปอยู่ที่ 80% ตัวอย่าง: 80'
      }
    },
    {
      name: 'averageScore',
      label: {
        en: 'Average Score (%)',
        th: 'คะแนนเฉลี่ย (%)'
      },
      type: 'number',
      min: 0,
      max: 100,
      tooltip: {
        en: 'Mean score achieved by all participants. Use to gauge overall training effectiveness. Example: 87.5',
        th: 'คะแนนเฉลี่ยที่ผู้เข้าร่วมทั้งหมดบรรลุ ใช้เพื่อวัดประสิทธิภาพการฝึกอบรมโดยรวม ตัวอย่าง: 87.5'
      }
    },
    {
      name: 'competencyAchieved',
      label: {
        en: 'Competency Achieved?',
        th: 'บรรลุความสามารถแล้วหรือยัง?'
      },
      type: 'select',
      options: [
        { value: 'yes', label: { en: 'Yes', th: 'ใช่' } },
        { value: 'no', label: { en: 'No', th: 'ไม่' } },
        { value: 'partial', label: { en: 'Partially', th: 'บางส่วน' } }
      ],
      tooltip: {
        en: 'Whether participants demonstrated required competencies. Yes: All learning objectives met. Partially: Most objectives met, some remediation needed. No: Significant gaps in knowledge or skills.',
        th: 'ไม่ว่าผู้เข้าร่วมจะแสดงความสามารถที่จำเป็น ใช่: บรรลุวัตถุประสงค์การเรียนรู้ทั้งหมด บางส่วน: บรรลุวัตถุประสงค์ส่วนใหญ่ ต้องการการปรับปรุงบางประการ ไม่: ช่องว่างที่สำคัญในความรู้หรือทักษะ'
      }
    },

    // Feedback Section
    {
      name: 'sectionHeader_feedback',
      label: {
        en: '=== Participant Feedback ===',
        th: '=== ข้อเสนอแนะจากผู้เข้าร่วม ==='
      },
      type: 'header'
    },
    {
      name: 'feedbackCollected',
      label: {
        en: 'Feedback Collected?',
        th: 'รวบรวมข้อเสนอแนะแล้วหรือยัง?'
      },
      type: 'select',
      options: [
        { value: 'yes', label: { en: 'Yes', th: 'ใช่' } },
        { value: 'no', label: { en: 'No', th: 'ไม่' } }
      ],
      required: true,
      tooltip: {
        en: 'Whether formal feedback was collected from participants. Essential for continuous improvement. Example: Yes - Distributed anonymous feedback forms to all 15 participants.',
        th: 'ไม่ว่าจะรวบรวมข้อเสนอแนะอย่างเป็นทางการจากผู้เข้าร่วมหรือไม่ สิ่งสำคัญสำหรับการปรับปรุงอย่างต่อเนื่อง ตัวอย่าง: ใช่ - แจกแบบฟอร์มข้อเสนอแนะไม่ระบุชื่อให้ผู้เข้าร่วมทั้ง 15 คน'
      }
    },
    {
      name: 'feedbackSummary',
      label: {
        en: 'Feedback Summary',
        th: 'สรุปข้อเสนอแนะ'
      },
      type: 'textarea',
      rows: 5,
      placeholder: {
        en: 'Summarize key feedback points from participants...',
        th: 'สรุปประเด็นข้อเสนอแนะหลักจากผู้เข้าร่วม...'
      },
      tooltip: {
        en: 'Consolidated summary of participant feedback. Highlight positive responses, constructive criticism, and suggestions for improvement. Include specific quotes when possible. Example: 90% rated content as very relevant. Common suggestion: Extend session by 30 minutes for hands-on practice.',
        th: 'สรุปรวมของข้อเสนอแนะจากผู้เข้าร่วม เน้นคำตอบเชิงบวก วิจารณ์เชิงสร้างสรรค์ และข้อเสนอแนะเพื่อการปรับปรุง รวมถึงคำพูดเฉพาะเมื่อเป็นไปได้ ตัวอย่าง: 90% ให้คะแนนเนื้อหาว่าเกี่ยวข้องมาก ข้อเสนอแนะทั่วไป: ขยายเซสชัน 30 นาทีสำหรับการฝึกฝนด้วยตนเอง'
      }
    },
    {
      name: 'satisfactionRating',
      label: {
        en: 'Overall Satisfaction Rating',
        th: 'คะแนนความพึงพอใจโดยรวม'
      },
      type: 'select',
      options: [
        { value: 'excellent', label: { en: 'Excellent (5)', th: 'ยอดเยี่ยม (5)' } },
        { value: 'very-good', label: { en: 'Very Good (4)', th: 'ดีมาก (4)' } },
        { value: 'good', label: { en: 'Good (3)', th: 'ดี (3)' } },
        { value: 'fair', label: { en: 'Fair (2)', th: 'พอใช้ (2)' } },
        { value: 'poor', label: { en: 'Poor (1)', th: 'แย่ (1)' } }
      ],
      tooltip: {
        en: 'Average satisfaction score based on participant feedback. 5-point scale where 5=Excellent and 1=Poor. Use to benchmark training effectiveness over time. Example: Very Good (4) - Strong content delivery, minor timing issues noted.',
        th: 'คะแนนความพึงพอใจเฉลี่ยตามข้อเสนอแนะจากผู้เข้าร่วม ระด	scale 5 คะแนนโดยที่ 5=ยอดเยี่ยม และ 1=แย่ ใช้เพื่อเปรียบเทียบประสิทธิภาพการฝึกอบรมเมื่อเวลาผ่านไป ตัวอย่าง: ดีมาก (4) - การส่งมอบเนื้อหาที่ดี ระบุปัญหาด้านเวลาเล็กน้อย'
      }
    },

    // Continuous Improvement Section
    {
      name: 'sectionHeader_improvement',
      label: {
        en: '=== Continuous Improvement ===',
        th: '=== การปรับปรุงอย่างต่อเนื่อง ==='
      },
      type: 'header'
    },
    {
      name: 'improvementSuggestions',
      label: {
        en: 'Improvement Suggestions',
        th: 'ข้อเสนอแนะในการปรับปรุง'
      },
      type: 'textarea',
      rows: 5,
      placeholder: {
        en: 'Suggestions for improving this training program based on feedback...',
        th: 'ข้อเสนอแนะในการปรับปรุงโปรแกรมการฝึกอบรมนี้ตามข้อเสนอแนะ...'
      },
      tooltip: {
        en: 'Actionable recommendations for enhancing future training sessions. Focus on content, delivery methods, timing, materials, and participant engagement. Example: Add 15-minute Q&A segment to each module. Replace outdated video clips with recent field footage.',
        th: 'คำแนะนำที่สามารถดำเนินการได้สำหรับการปรับปรุงเซสชันการฝึกอบรมในอนาคต เน้นที่เนื้อหา วิธีการส่งมอบ เวลา วัสดุ และการมีส่วนร่วมของผู้เข้าร่วม ตัวอย่าง: เพิ่มเซ็กเมนต์ถาม-ตอบ 15 นาทีในแต่ละโมดูล แทนที่วิดีโอเก่าด้วยภาพจากสนามที่เพิ่งถ่ายทำ'
      }
    },
    {
      name: 'followUpActions',
      label: {
        en: 'Follow-up Actions Required',
        th: 'การดำเนินการติดตามที่จำเป็น'
      },
      type: 'textarea',
      rows: 4,
      placeholder: {
        en: 'List any follow-up actions needed...',
        th: 'ระบุการดำเนินการติดตามที่จำเป็น...'
      },
      tooltip: {
        en: 'Specific tasks arising from this training session that require completion. Assign responsible persons and set realistic deadlines. Example: Update PPE SOP by Nov 15, 2023. Schedule refresher training for all field staff by Dec 1, 2023.',
        th: 'งานเฉพาะที่เกิดขึ้นจากเซสชันการฝึกอบรมครั้งนี้ที่ต้องการการเสร็จสิ้น มอบหมายบุคคลที่รับผิดชอบและกำหนดระยะเวลาที่เป็นจริง ตัวอย่าง: อัปเดตข้อกำหนด PPE ภายใน 15 พ.ย. 2023 กำหนดการฝึกอบรมทบทวนให้บุคลากรในสนามทั้งหมดภายใน 1 ธ.ค. 2023'
      }
    },
    {
      name: 'nextTrainingDate',
      label: {
        en: 'Next Training Session Scheduled',
        th: 'กำหนดการฝึกอบรมครั้งต่อไป'
      },
      type: 'date',
      tooltip: {
        en: 'Date when the next related training session is planned. Important for compliance tracking. Example: 2023-12-01',
        th: 'วันที่ที่วางแผนไว้สำหรับการฝึกอบรมที่เกี่ยวข้องครั้งต่อไป สิ่งสำคัญสำหรับการติดตามการปฏิบัติตามข้อกำหนด ตัวอย่าง: 2023-12-01'
      }
    },
    {
      name: 'trainerNotes',
      label: {
        en: 'Trainer Notes',
        th: 'หมายเหตุของผู้ฝึก'
      },
      type: 'textarea',
      rows: 4,
      placeholder: {
        en: 'Additional notes from the trainer...',
        th: 'หมายเหตุเพิ่มเติมจากผู้ฝึก...'
      },
      tooltip: {
        en: 'Personal observations, insights, or administrative notes from the trainer. Include class dynamics, notable participant questions, or logistical issues. Example: High engagement during hands-on segment. Participant #7 asked excellent question about cross-contamination prevention.',
        th: 'ข้อสังเกตส่วนตัว ข้อมูลเชิงลึก หรือหมายเหตุทางการบริหารจากผู้ฝึก รวมถึงพลวัติชั้นเรียน คำถามที่น่าสนใจจากผู้เข้าร่วม หรือปัญหาด้านโลจิสติกส์ ตัวอย่าง: การมีส่วนร่วมสูงระหว่างเซ็กเมนต์ปฏิบัติจริง ผู้เข้าร่วม #7 ถามคำถามที่ยอดเยี่ยมเกี่ยวกับการป้องกันการปนเปื้อน'
      }
    },

    // Certification/Documentation Section
    {
      name: 'sectionHeader_certification',
      label: {
        en: '=== Certification & Documentation ===',
        th: '=== การรับรองและการบันทึกเอกสาร ==='
      },
      type: 'header'
    },
    {
      name: 'certificatesIssued',
      label: {
        en: 'Certificates Issued?',
        th: 'ออกใบรับรองแล้วหรือยัง?'
      },
      type: 'select',
      options: [
        { value: 'yes', label: { en: 'Yes', th: 'ใช่' } },
        { value: 'no', label: { en: 'No', th: 'ไม่' } }
      ],
      tooltip: {
        en: 'Whether completion certificates were distributed to participants who met competency requirements. Important for compliance and career development tracking. Example: Yes - All 15 participants who scored 80%+ received certificates.',
        th: 'ไม่ว่าจะแจกใบรับรองแก่ผู้เข้าร่วมที่ตรงตามข้อกำหนดความสามารถหรือไม่ สิ่งสำคัญสำหรับการติดตามการปฏิบัติตามข้อกำหนดและการพัฒนาอาชีพ ตัวอย่าง: ใช่ - ผู้เข้าร่วมทั้ง 15 คนที่ได้คะแนน 80%+ ได้รับใบรับรอง'
      }
    },
    {
      name: 'certificateNumbers',
      label: {
        en: 'Certificate Numbers (if applicable)',
        th: 'หมายเลขใบรับรอง (หากระบุไว้)'
      },
      type: 'textarea',
      rows: 3,
      placeholder: {
        en: 'List certificate numbers issued...',
        th: 'ระบุหมายเลขใบรับรองที่ออก...'
      },
      tooltip: {
        en: 'Sequential or batch numbers of certificates issued. Useful for tracking and verification. Example: TRN-2023-1015-001 through TRN-2023-1015-015',
        th: 'ลำดับต่อเนื่องหรือชุดของหมายเลขใบรับรองที่ออก ประโยชน์สำหรับการติดตามและการตรวจสอบ ตัวอย่าง: TRN-2023-1015-001 ถึง TRN-2023-1015-015'
      }
    },
    {
      name: 'documentationCompleted',
      label: {
        en: 'Training Documentation Completed?',
        th: 'บันทึกเอกสารการฝึกอบรมแล้วหรือยัง?'
      },
      type: 'select',
      options: [
        { value: 'yes', label: { en: 'Yes', th: 'ใช่' } },
        { value: 'no', label: { en: 'No', th: 'ไม่' } }
      ],
      required: true,
      tooltip: {
        en: 'Confirmation that all required training documentation has been completed and filed. Critical for audit readiness. Example: Yes - All forms scanned and uploaded to HSE Management System.',
        th: 'การยืนยันว่าเอกสารการฝึกอบรมที่จำเป็นทั้งหมดได้เสร็จสิ้นและจัดเก็บแล้ว สิ่งสำคัญสำหรับการเตรียมความพร้อมการตรวจสอบ ตัวอย่าง: ใช่ - แบบฟอร์มทั้งหมดสแกนและอัปโหลดสู่ระบบการจัดการ HSE'
      }
    },
    {
      name: 'documentsFiled',
      label: {
        en: 'Documents Filed in Records System',
        th: 'เอกสารที่จัดเก็บในระบบบันทึก'
      },
      type: 'select',
      options: [
        { value: 'yes', label: { en: 'Yes', th: 'ใช่' } },
        { value: 'no', label: { en: 'No', th: 'ไม่' } }
      ],
      tooltip: {
        en: 'Whether physical and digital training records have been properly archived. Important for compliance audits and legal requirements. Example: Yes - Paper files stored in locked cabinet, digital copies in SharePoint archive.',
        th: 'ไม่ว่าจะจัดเก็บเอกสารการฝึกอบรมแบบกายภาพและดิจิทัลอย่างเหมาะสมหรือไม่ สิ่งสำคัญสำหรับการตรวจสอบการปฏิบัติตามข้อกำหนดและข้อกำหนดทางกฎหมาย ตัวอย่าง: ใช่ - เอกสารกระดาษจัดเก็บในตู้ล็อค สำเนาดิจิทัลในคลังเก็บข้อมูล SharePoint'
      }
    }
  ]
});

export default getTrainingRecordFormTemplate;